import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import SignaturePad from 'signature_pad';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Printer, Save, WifiOff, Wifi, Eraser, Lock, ExternalLink, ClipboardCheck } from 'lucide-react';
import { formatDate, formatDateTime } from '../lib/format';

const SIG_ROLES = [
  { key: 'coordinator', label: 'Equipment Coordinator', desc: 'Released by' },
  { key: 'operator', label: 'Operator (Received Equipment)', desc: 'Operator acknowledgement' },
  { key: 'receiver', label: 'Destination Site Receiver', desc: 'On-site receiver' },
];

const FORM_FIELDS = [
  'project_name', 'project_address', 'requested_category', 'requested_description',
  'date_required', 'duration_from', 'duration_to',
  'from_location', 'to_location', 'travel_plan',
  'requested_by', 'approved_by', 'current_location_label',
];

const lsKey = (id) => `mob_form_draft:${id}`;

const MobilizationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [mob, setMob] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [operator, setOperator] = useState(null);
  const [branding, setBranding] = useState(null);
  const [form, setForm] = useState({});
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);
  // Only Super Admins can clear a captured signature, and only while the
  // mobilization is not yet completed. Once completed, signatures are locked
  // by the backend for non-repudiation.
  const isMobCompleted = mob?.status === 'completed';
  const canClearSignatures = hasRole(user, 'super_admin') && !isMobCompleted;

  const load = useCallback(async () => {
    try {
      const m = await api.get(`/mobilizations/${id}`);
      setMob(m.data);
      const initial = {};
      FORM_FIELDS.forEach((k) => { initial[k] = m.data[k] || ''; });
      // hydrate from localStorage draft if it's newer
      const draft = localStorage.getItem(lsKey(id));
      if (draft) {
        try { Object.assign(initial, JSON.parse(draft)); } catch (e) { console.warn('MobilizationForm draft parse failed:', e?.message || e); }
      }
      setForm(initial);
      if (m.data.vehicle_id) {
        try { const v = await api.get(`/vehicles/${m.data.vehicle_id}`); setVehicle(v.data); } catch (e) { console.warn('MobilizationForm vehicle fetch failed:', e?.message || e); }
      }
      if (m.data.operator_id) {
        try {
          const ops = await api.get('/operators'); setOperator(ops.data.find((o) => o.id === m.data.operator_id));
        } catch (e) { console.warn('MobilizationForm operator fetch failed:', e?.message || e); }
      }
      const b = await api.get('/settings/branding'); setBranding(b.data);
    } catch (e) {
      toast({ title: 'Failed to load mobilization', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  // online/offline tracking
  useEffect(() => {
    const onOnline = () => { setOnline(true); if (pendingSync) flushDraft(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
    // eslint-disable-next-line
  }, [pendingSync]);

  const setField = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      localStorage.setItem(lsKey(id), JSON.stringify(next));
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => flushDraft(next), 1800);
      return next;
    });
  };

  const flushDraft = async (payloadArg) => {
    const payload = payloadArg || form;
    if (!online) { setPendingSync(true); return; }
    try {
      await api.put(`/mobilizations/${id}/form`, payload);
      setSavedAt(new Date());
      setPendingSync(false);
      localStorage.removeItem(lsKey(id));
    } catch (e) {
      setPendingSync(true);
    }
  };

  const trackingUrl = branding?.tracking_base_url
    ? `${branding.tracking_base_url.replace(/\/$/, '')}/${mob?.public_token || ''}`
    : `${window.location.origin}/esd-track/${mob?.public_token || ''}`;

  if (!mob) {
    return <div className="p-8 text-center text-slate-500">Loading mobilization form…</div>;
  }

  return (
    <div className="space-y-4">
      {/* On-screen action bar (hidden when printing) */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-2 border-b pb-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/esd/mobilizations')} data-testid="mob-form-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 text-xs">
            {online ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded"><Wifi className="h-3.5 w-3.5" /> Online</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded"><WifiOff className="h-3.5 w-3.5" /> Offline — saving locally</span>
            )}
            {pendingSync && online && <span className="text-amber-700 text-xs">Sync pending…</span>}
            {savedAt && !pendingSync && online && <span className="text-slate-500 text-xs">Saved {savedAt.toLocaleTimeString()}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => flushDraft()} data-testid="mob-form-save-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(trackingUrl, '_blank')} data-testid="mob-form-open-tracker-btn"><ExternalLink className="h-4 w-4 mr-1" /> Open Tracker</Button>
          <Button size="sm" onClick={() => window.print()} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="mob-form-print-btn"><Printer className="h-4 w-4 mr-1" /> Print / Save as PDF</Button>
        </div>
      </div>

      {!online && (
        <Alert className="print:hidden bg-amber-50 border-amber-300 text-amber-900">
          <AlertDescription className="text-sm">
            You're offline. Edits are auto-saved locally to your device and will sync when the connection returns.
          </AlertDescription>
        </Alert>
      )}

      {/* PRINTABLE FORM */}
      <div className="bg-white border border-slate-300 print:border-0 mx-auto max-w-4xl text-slate-900" id="mob-printable" data-testid="mob-printable">
        {/* Header */}
        <div className="px-8 py-6 border-b-2 border-slate-900 flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="h-14 w-14 object-contain" />
            ) : (
              <div className="h-14 w-14 rounded bg-[hsl(217,91%,24%)] grid place-items-center text-white font-bold text-lg">PIVI</div>
            )}
            <div>
              <h1 className="text-xl font-bold tracking-tight">{branding?.subtitle || 'EQUIPMENT MOBILIZATION FORM (ETF + EMF + IETF)'}</h1>
              <div className="text-xs uppercase tracking-wider text-slate-600">{branding?.header_text || 'PIVI EQUIPMENT SOLUTIONS DEPARTMENT'}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Generated: {formatDateTime(new Date().toISOString())}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-slate-500">Serial Number</div>
            <div className="font-mono text-base font-bold tracking-wider text-[hsl(217,91%,24%)]" data-testid="mob-serial">{mob.serial_number || mob.reference_number}</div>
            <div className="mt-2 inline-block p-1.5 bg-white border border-slate-300 rounded">
              <QRCodeCanvas value={trackingUrl} size={92} level="M" includeMargin={false} />
            </div>
            <div className="text-[9px] text-slate-500 mt-1 max-w-[120px] ml-auto leading-tight">Scan for live tracking</div>
          </div>
        </div>

        {/* Section 1: Equipment Request Information */}
        <Section title="EQUIPMENT REQUEST INFORMATION">
          <Field label="Reference Number" value={mob.reference_number} mono />
          <Field label="Project Name" value={form.project_name} onChange={(v) => setField('project_name', v)} testId="field-project_name" />
          <Field label="Project Address" value={form.project_address} onChange={(v) => setField('project_address', v)} testId="field-project_address" colSpan={2} />
          <Field label="Equipment Category" value={form.requested_category} onChange={(v) => setField('requested_category', v)} testId="field-requested_category" />
          <Field label="Equipment Description" value={form.requested_description} onChange={(v) => setField('requested_description', v)} testId="field-requested_description" />
          <Field label="Date Required" value={form.date_required} onChange={(v) => setField('date_required', v)} testId="field-date_required" type="date" />
          <Field label="Duration Period" value={`${form.duration_from || ''} to ${form.duration_to || ''}`}
            customInput={(
              <div className="flex items-center gap-2">
                <Input type="date" value={form.duration_from || ''} onChange={(e) => setField('duration_from', e.target.value)} className="h-8 text-sm" data-testid="field-duration_from" />
                <span className="text-xs text-slate-500">to</span>
                <Input type="date" value={form.duration_to || ''} onChange={(e) => setField('duration_to', e.target.value)} className="h-8 text-sm" data-testid="field-duration_to" />
              </div>
            )}
          />
        </Section>

        {/* Section 2: Equipment & Operator Assignment */}
        <Section title="EQUIPMENT & OPERATOR ASSIGNMENT">
          <Field label="Equipment Plate Number" value={vehicle?.plate_no} mono />
          <Field label="Equipment Code" value={vehicle?.equipment_code} mono />
          <Field label="Equipment Model" value={vehicle?.model} />
          <Field label="Equipment Brand" value={vehicle?.brand || vehicle?.make} />
          <Field label="Equipment Description" value={vehicle?.description} colSpan={2} />
          <Field label="Company Assigned" value={vehicle?.company_assigned} />
          <Field label="Operator Name" value={operator?.full_name || '—'} />
          <Field label="License Number" value={operator?.license_no || '—'} mono />
          <Field label="Contact Information" value={operator?.contact_no || '—'} />
        </Section>

        {/* Section 3: Mobilization Schedule & Route */}
        <Section title="MOBILIZATION SCHEDULE & ROUTE">
          <Field label="From Location" value={form.from_location} onChange={(v) => setField('from_location', v)} testId="field-from_location" />
          <Field label="To Location" value={form.to_location} onChange={(v) => setField('to_location', v)} testId="field-to_location" />
          <Field label="Scheduled Departure" value={mob.scheduled_departure ? formatDate(mob.scheduled_departure) : '—'} />
          <Field label="Scheduled Arrival" value={mob.scheduled_arrival ? formatDate(mob.scheduled_arrival) : '—'} />
          <div className="col-span-2">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Travel Plan</Label>
            <Textarea rows={2} value={form.travel_plan || ''} onChange={(e) => setField('travel_plan', e.target.value)} placeholder="Route description, checkpoints, escorts…" className="text-sm" data-testid="field-travel_plan" />
          </div>
        </Section>

        {/* Section 4: Approvals & Signatures */}
        <Section title="APPROVALS & SIGNATURES">
          <Field label="Requested By" value={form.requested_by} onChange={(v) => setField('requested_by', v)} testId="field-requested_by" />
          <Field label="Request Date" value={mob.created_at ? formatDate(mob.created_at) : '—'} />
          <Field label="Approved By" value={form.approved_by} onChange={(v) => setField('approved_by', v)} testId="field-approved_by" />
          <Field label="Approval Date" value={mob.created_at ? formatDate(mob.created_at) : '—'} />
          <div className="col-span-2 flex items-center gap-2">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Mobilization Status:</Label>
            <Badge className="bg-emerald-100 text-emerald-800 uppercase tracking-wider" data-testid="mob-status-badge">{mob.status}</Badge>
          </div>
        </Section>

        {/* Section 5: Acknowledgement & Handover (with signature pads) */}
        <div className="px-8 py-4 border-t-2 border-slate-900 mt-4 print:break-before-page">
          <h2 className="text-sm font-bold tracking-wider mb-3 text-slate-800">ACKNOWLEDGEMENT & HANDOVER</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SIG_ROLES.map((r) => (
              <SignatureBlock key={r.key} mobId={id} role={r} sig={mob.signatures?.[r.key]} canAdmin={canClearSignatures} isCompleted={isMobCompleted} onUpdate={load} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-3 border-t text-[10px] text-center text-slate-500 italic">
          {branding?.footer_disclaimer || 'This is a computer-generated document and does not require a signature for reference purposes.'}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="px-8 py-4 border-t border-slate-300">
    <h2 className="text-sm font-bold tracking-wider mb-3 text-slate-800">{title}</h2>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, mono = false, type = 'text', testId, customInput, colSpan = 1 }) => (
  <div className={colSpan === 2 ? 'col-span-2' : ''}>
    <Label className="text-[11px] uppercase tracking-wide text-slate-500">{label}</Label>
    {customInput ? customInput : onChange ? (
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className={`h-8 text-sm ${mono ? 'font-mono' : ''}`} data-testid={testId} />
    ) : (
      <div className={`text-sm py-1.5 border-b border-dotted border-slate-300 min-h-[28px] ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    )}
  </div>
);

const SignatureBlock = ({ mobId, role, sig, canAdmin, isCompleted, onUpdate }) => {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const locked = !!sig;

  useEffect(() => {
    if (locked || !canvasRef.current) return;
    const c = canvasRef.current;
    // size canvas for crisp drawing
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    c.getContext('2d').scale(ratio, ratio);
    padRef.current = new SignaturePad(c, { backgroundColor: 'rgba(255,255,255,0)', penColor: '#0f172a', minWidth: 0.6, maxWidth: 1.6 });
    return () => { try { padRef.current?.off(); } catch (e) { console.warn('SignaturePad cleanup failed:', e?.message || e); } };
  }, [locked]);

  const clear = () => padRef.current?.clear();

  const save = async () => {
    if (!padRef.current || padRef.current.isEmpty()) { toast({ title: 'Please sign before saving', variant: 'destructive' }); return; }
    if (!name.trim()) { toast({ title: 'Please enter your name', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const dataUrl = padRef.current.toDataURL('image/png');
      await api.put(`/mobilizations/${mobId}/signatures`, { role: role.key, name: name.trim(), signature_png: dataUrl });
      toast({ title: `${role.label} signature captured` });
      onUpdate();
    } catch (e) {
      toast({ title: 'Could not save signature', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const clearLocked = async () => {
    if (!window.confirm(`Clear the ${role.label} signature…`)) return;
    try {
      await api.post(`/mobilizations/${mobId}/signatures/${role.key}/clear`);
      toast({ title: 'Signature cleared' });
      onUpdate();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' });
    }
  };

  return (
    <div className="border border-slate-300 rounded p-3 bg-white" data-testid={`sig-block-${role.key}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{role.label}</div>
      <div className="text-[10px] text-slate-400">{role.desc}</div>
      {locked ? (
        <div className="mt-2 space-y-2">
          <div className="h-20 bg-slate-50 rounded border border-slate-200 grid place-items-center">
            <img src={sig.signature_png} alt="signature" className="max-h-16 object-contain" data-testid={`sig-img-${role.key}`} />
          </div>
          <div className="text-xs">
            <div className="font-semibold text-slate-800">{sig.name}</div>
            <div className="text-[10px] text-slate-500">Signed {formatDateTime(sig.signed_at)}</div>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-emerald-700 print:hidden"><Lock className="h-3 w-3" /> Locked</div>
          {isCompleted && (
            <div className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 print:hidden" data-testid={`sig-iso-lock-${role.key}`}>
              <Lock className="h-3 w-3 mt-0.5 shrink-0" />
              <span>Locked post-completion — non-repudiation applies. Not clearable, even by Super Admin.</span>
            </div>
          )}
          {canAdmin && (
            <Button size="sm" variant="ghost" onClick={clearLocked} className="text-xs print:hidden" data-testid={`sig-clear-${role.key}`}>
              <Eraser className="h-3.5 w-3.5 mr-1" /> Clear (super admin)
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-2 print:hidden">
          {isCompleted ? (
            <div className="h-20 rounded border border-dashed border-slate-300 bg-slate-50 grid place-items-center text-[10px] text-slate-500 italic" data-testid={`sig-post-completion-block-${role.key}`}>
              Signatures locked — mobilization already completed.
            </div>
          ) : (
            <>
              <canvas ref={canvasRef} className="w-full h-20 border border-slate-300 rounded bg-white touch-none" data-testid={`sig-canvas-${role.key}`} />
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your name" className="h-8 text-xs" data-testid={`sig-name-${role.key}`} />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={clear} className="text-xs" data-testid={`sig-erase-${role.key}`}><Eraser className="h-3 w-3 mr-1" /> Erase</Button>
                <Button size="sm" disabled={busy} onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)] text-xs" data-testid={`sig-save-${role.key}`}>
                  <ClipboardCheck className="h-3 w-3 mr-1" /> {busy ? 'Saving…' : 'Capture'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {/* When printed (and unsigned), show empty signature line */}
      {!locked && (
        <div className="mt-2 hidden print:block">
          <div className="border-b border-slate-700 h-12" />
          <div className="text-[10px] text-slate-500 mt-1">Name & Signature — Date: ____________</div>
        </div>
      )}
    </div>
  );
};

export default MobilizationForm;
